import { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import * as microsoftTeams from '@microsoft/teams-js';
import { tokenRequest } from './msalConfig';
import { isInteractionRequired } from './authErrors';
import type { AuthContextType, UserInfo } from './types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Detects if the app is running inside Microsoft Teams
 */
function isInTeams(): boolean {
  return window.parent !== window.self && window.name === 'embedded-page-container';
}

/**
 * AuthProvider component that wraps the app and provides authentication context
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || null);
  
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isTeamsContext, setIsTeamsContext] = useState<boolean>(false);
  const acquireTokenInFlight = useRef<Promise<string | null> | null>(null);

  // Initialize Teams context if running in Teams
  useEffect(() => {
    if (isInTeams()) {
      microsoftTeams.app.initialize().then(() => {
        setIsTeamsContext(true);
        console.log('Teams context initialized');
      }).catch((error) => {
        console.error('Teams initialization error:', error);
      });
    }
  }, []);

  // Update user info when account changes
  useEffect(() => {
    if (account) {
      setUser({
        username: account.username,
        name: account.name || account.username,
        email: account.username,
        roles: account.idTokenClaims?.roles as string[] | undefined,
      });
      setIsLoading(false);
    } else {
      setUser(null);
      // Only set loading to false if no interaction is in progress
      if (inProgress === InteractionStatus.None) {
        setIsLoading(false);
      }
    }
  }, [account, inProgress]);

  /**
   * Login using redirect flow
   */
  const login = async (): Promise<void> => {
    try {
      setError(null);
      sessionStorage.removeItem('auth:token_expired');
      await instance.loginRedirect({
        scopes: tokenRequest.scopes,
      });
    } catch (error) {
      console.error('Login error:', error);
      setError(error instanceof Error ? error.message : 'Login failed');
      throw error;
    }
  };

  /**
   * Logout and clear cache
   */
  const logout = async (): Promise<void> => {
    try {
      setError(null);
      await instance.logoutRedirect({
        account: account || undefined,
      });
    } catch (error) {
      console.error('Logout error:', error);
      setError(error instanceof Error ? error.message : 'Logout failed');
      throw error;
    }
  };

  const forceReLogin = async (): Promise<void> => {
    sessionStorage.setItem('auth:token_expired', 'true');
    setError('Your session has expired. Please log in again.');
    try {
      await instance.logoutRedirect({
        account: account || undefined,
        postLogoutRedirectUri: `${window.location.origin}/login`,
      });
    } catch (logoutError) {
      console.error('Logout failed:', logoutError);
      instance.setActiveAccount(null);
      window.location.href = '/login';
    }
  };

  /**
   * Acquire token silently (cache / iframe only). Does not trigger interactive login or logout.
   */
  const acquireTokenSilent = async (): Promise<string | null> => {
    if (!account) {
      console.warn('No account available for token acquisition');
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: account,
      });
      return response.accessToken;
    } catch (error) {
      console.error('Silent token acquisition failed:', error);
      return null;
    }
  };

  /**
   * Acquire token with interactive fallback (popup / Teams SSO).
   * Logs out and redirects to login only if interactive acquisition also fails.
   */
  const acquireToken = async (): Promise<string | null> => {
    if (acquireTokenInFlight.current) {
      return acquireTokenInFlight.current;
    }

    acquireTokenInFlight.current = (async () => {
      if (!account) {
        console.warn('No account available for token acquisition');
        return null;
      }

      try {
        const silentResponse = await instance.acquireTokenSilent({
          ...tokenRequest,
          account,
        });
        return silentResponse.accessToken;
      } catch (silentError) {
        console.error('Silent token acquisition failed:', silentError);

        // Any silent failure (including iframe timeouts) — try popup or Teams SSO
        if (!isTeamsContext) {
          try {
            const popupResponse = await instance.acquireTokenPopup({
              ...tokenRequest,
              account,
            });
            return popupResponse.accessToken;
          } catch (popupError) {
            console.error('Popup token acquisition failed:', popupError);
            await forceReLogin();
            return null;
          }
        }

        try {
          console.log('Attempting Teams SSO token acquisition');
          return await microsoftTeams.authentication.getAuthToken({
            resources: [import.meta.env.VITE_AZURE_SCOPE],
          });
        } catch (teamsError) {
          console.error('Teams SSO failed:', teamsError);
          await forceReLogin();
          return null;
        }
      }
    })();

    try {
      return await acquireTokenInFlight.current;
    } finally {
      acquireTokenInFlight.current = null;
    }
  };

  /**
   * Refresh token and update user roles
   * Forces a new token acquisition to get updated roles/claims
   */
  const refreshToken = async (): Promise<void> => {
    if (!account) {
      console.warn('No account available for token refresh');
      return;
    }

    try {
      setError(null);
      setIsLoading(true);
      
      const silentRequest = {
        ...tokenRequest,
        account: account,
        forceRefresh: true,
      };
      
      const response = await instance.acquireTokenSilent(silentRequest);
      
      if (response.account) {
        setUser({
          username: response.account.username,
          name: response.account.name || response.account.username,
          email: response.account.username,
          roles: response.account.idTokenClaims?.roles as string[] | undefined,
        });
      }
      
      console.log('Token refreshed successfully');
    } catch (error) {
      if (isInteractionRequired(error)) {
        try {
          const response = await instance.acquireTokenPopup({
            ...tokenRequest,
            account: account,
          });
          
          if (response.account) {
            setUser({
              username: response.account.username,
              name: response.account.name || response.account.username,
              email: response.account.username,
              roles: response.account.idTokenClaims?.roles as string[] | undefined,
            });
          }
          
          console.log('Token refreshed via popup');
        } catch (popupError) {
          console.error('Token refresh popup failed:', popupError);
          setError(popupError instanceof Error ? popupError.message : 'Token refresh failed');
        }
      } else {
        console.error('Token refresh error:', error);
        setError(error instanceof Error ? error.message : 'Token refresh failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: AuthContextType = {
    isAuthenticated: !!account,
    isLoading,
    user,
    error,
    login,
    logout,
    acquireToken,
    acquireTokenSilent,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to access the auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
